﻿import * as MUI from "@material-ui/core";
import * as React from "react";
import {
    FileDisplay,
    FileElement,
    FileUpload,
    TextField,
    ThematicBreak,
} from "../Components";
import { Dictionary } from "../../Utilities";
import { IReturnModel } from "../IReturnModel";

export interface IRequestFormProps {
    showErrorPageCallback: () => void;
}

interface IRequestDTO {
    comments: string;
    description: string;
    mainUrl: string;
    selectedModule: string;
    title: string;
}

export interface IRequestFormState {
    areUploaded: Dictionary<string, boolean>;
    attachments: Dictionary<string, File>;
    comments: string;
    description: string;
    descriptionError: string;
    errorMessage: string;
    mainUrl: string;
    moduleError: string;
    moduleOptions: string[];
    progress: "input" | "loading" | "confirmation";
    selectedModule: string;
    title: string;
    titleError: string;
}

interface IInteralRequestFormProps {
    areUploaded: Dictionary<string, boolean>;
    attachments: Dictionary<string, File>;
    comments: string;
    description: string;
    descriptionError: string;
    errorMessage: string;
    handleCommentsChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    handleDescriptionChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    handleModuleChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    handleTitleChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    loadFile: (file: File) => void;
    mainUrl: string;
    moduleError: string;
    moduleOptions: string[];
    onUploadComplete: (fileName: string) => void;
    postRequest: () => Promise<void>;
    selectedModule: string;
    title: string;
    titleError: string;
    unloadFile: (file: File) => void;
}

export class RequestForm extends React.Component<
    IRequestFormProps,
    IRequestFormState
> {
    constructor(props: IRequestFormProps) {
        super(props);

        this.state = {
            areUploaded: new Dictionary<string, boolean>(),
            attachments: new Dictionary<string, File>(),
            comments: ``,
            description: ``,
            descriptionError: null,
            errorMessage: null,
            mainUrl: MAIN_URL,
            moduleError: null,
            moduleOptions: [],
            progress: `input`,
            selectedModule: ``,
            title: ``,
            titleError: null,
        };
    }

    componentDidMount(): void {
        void this._getInitialState();
    }

    private readonly _getInitialState = async (): Promise<void> => {
        const stringResult: string = await $.ajax(`/Support/FeatureRequest`, {
            method: `GET`,
        });

        const result: IReturnModel<IRequestFormState> =
            JSON.parse(stringResult);

        this.setState({
            moduleOptions: result.value.moduleOptions,
        });
    };

    private readonly _handleTitleChange = (
        e: React.ChangeEvent<HTMLInputElement>,
    ): void => {
        const value: string = e.target.value;

        this.setState({
            title: value,
        });
    };

    private readonly _handleDescriptionChange = (
        e: React.ChangeEvent<HTMLInputElement>,
    ): void => {
        const value: string = e.target.value;

        this.setState({
            description: value,
        });
    };

    private readonly _handleModuleChange = (
        e: React.ChangeEvent<HTMLInputElement>,
    ): void => {
        const value: string = e.target.value;

        this.setState({
            selectedModule: value,
        });
    };

    private readonly _handleCommentsChange = (
        e: React.ChangeEvent<HTMLInputElement>,
    ): void => {
        const value: string = e.target.value;

        this.setState({
            comments: value,
        });
    };

    private readonly _loadFile = (file: File): void => {
        this.setState((prev) => {
            const files: Dictionary<string, File> = prev.attachments,
                uploadedStatuses: Dictionary<string, boolean> =
                    prev.areUploaded;

            files.push(file.name, file);
            uploadedStatuses.push(file.name, false);

            return {
                areUploaded: uploadedStatuses,
                attachments: files,
            };
        });
    };

    private readonly _unloadFile = (file: File): void => {
        this.setState((prev) => {
            const files: Dictionary<string, File> = prev.attachments,
                uploadedStatuses: Dictionary<string, boolean> =
                    prev.areUploaded;

            files.remove(file.name);
            uploadedStatuses.remove(file.name);

            return {
                areUploaded: uploadedStatuses,
                attachments: files,
            };
        });
    };

    private readonly _onUploadComplete = (fileName: string): void => {
        this.setState((prev) => {
            const uploadedStatuses: Dictionary<string, boolean> =
                prev.areUploaded;

            uploadedStatuses.set(fileName, true);

            return {
                areUploaded: uploadedStatuses,
            };
        });
    };

    private readonly _postRequest = async (): Promise<void> => {
        const descriptionError: boolean = this.state.description === ``,
            moduleError: boolean = this.state.selectedModule === ``,
            titleError: boolean = this.state.title === ``;

        this.setState({
            descriptionError: descriptionError
                ? `Please fill in Feature Description`
                : null,
            moduleError: moduleError ? `Please choose a Related Module` : null,
            titleError: titleError ? `Please fill in Feature Summary` : null,
        });

        if (titleError || descriptionError || moduleError) {
            return;
        }

        const postData: IRequestDTO = {
            comments: this.state.comments,
            description: this.state.description,
            mainUrl: this.state.mainUrl,
            selectedModule: this.state.selectedModule,
            title: this.state.title,
        };

        this.setState({
            progress: `loading`,
        });

        const resultString: string = await $.ajax(`/Support/SubmitRequest`, {
            data: postData,
            method: `POST`,
        });

        const result: IReturnModel<null> = JSON.parse(resultString);

        if (result.isSuccess) {
            this.setState({
                progress: `confirmation`,
            });
        } else if (result.errorMessage !== `` && result.errorMessage !== null) {
            this.setState({
                errorMessage: result.errorMessage,
            });
        } else {
            this.props.showErrorPageCallback();
        }
    };

    private readonly _requestForm = (
        props: IInteralRequestFormProps,
    ): JSX.Element => {
        let isSubitEnabled = true;

        const isUploaded: (key: string, value: boolean) => void = (
            key: string,
            value: boolean,
        ) => {
            if (!value) {
                isSubitEnabled = value;
            }
        };

        props.areUploaded.forEach(isUploaded);

        return (
            <React.Fragment>
                <p>
                    Please use the form below to submit your request for a new
                    feature to be developed for Metricell SmartTools.
                    <br />
                    <br />
                    All fields marked with * are mandatory.
                </p>
                <h3>Feature Request</h3>
                <TextField
                    characterlimit={40}
                    className={`smartsupport-textinput`}
                    error={props.titleError !== null}
                    label={props.titleError || `Feature Summary*`}
                    onChange={props.handleTitleChange}
                    value={props.title}
                />
                <MUI.FormControl
                    className={`smartsupport-formcontrol`}
                    error={props.moduleError != null}
                >
                    <MUI.InputLabel id={`frequency-label`}>
                        {props.moduleError || `Related Module*`}
                    </MUI.InputLabel>
                    <MUI.Select
                        className={`smartsupport-select`}
                        labelId={`frequency-label`}
                        onChange={props.handleModuleChange}
                        value={props.selectedModule}
                    >
                        {props.moduleOptions &&
                            props?.moduleOptions.map(
                                (o): JSX.Element => (
                                    <MUI.MenuItem key={o} value={o}>
                                        {o}
                                    </MUI.MenuItem>
                                ),
                            )}
                    </MUI.Select>
                </MUI.FormControl>
                <TextField
                    characterlimit={300}
                    className={`smartsupport-textinput`}
                    error={props.descriptionError !== null}
                    inputProps={{ style: { overflow: `auto` } }}
                    label={props.descriptionError || `Feature Description*`}
                    multiline
                    onChange={props.handleDescriptionChange}
                    rows={`4`}
                    value={props.description}
                />
                <TextField
                    characterlimit={200}
                    className={`smartsupport-textinput`}
                    inputProps={{ style: { overflow: `auto` } }}
                    label={`Additional Comments`}
                    multiline
                    onChange={props.handleCommentsChange}
                    rows={`3`}
                    value={props.comments}
                />
                <div className={`filecontainer`}>
                    {props.attachments.map(
                        (n, f): JSX.Element => (
                            <FileElement
                                file={f}
                                key={n}
                                onCompleteCallback={props.onUploadComplete}
                                postURL={`/Support/Attachment`}
                                removeFileCallback={props.unloadFile}
                            />
                        ),
                    )}
                </div>
                <FileUpload fileLoadCallback={props.loadFile} />
                <MUI.Button
                    className={`smartsupport-submit`}
                    color={`primary`}
                    disabled={!isSubitEnabled}
                    onClick={props.postRequest}
                >
                    Submit
                </MUI.Button>
            </React.Fragment>
        );
    };

    private _awaitingResponce(): JSX.Element {
        return (
            <div className={`support-loadingcontainer`}>
                <img
                    className={`support-loading`}
                    src={`/Images/LoadingAnimation.svg`}
                />
            </div>
        );
    }

    private _requestSubmitted(props: IRequestFormState): JSX.Element {
        return (
            <React.Fragment>
                <img src={`/Images/SupportConfirmation.svg`} />
                <p>
                    Thank you for raising your feature request to Metricell.
                    This will be reviewed by our product team. A summary of your
                    request is down below:
                </p>
                <ThematicBreak />
                <h2>Feature Request</h2>
                <div className={`smartsupport-summary`}>
                    <MUI.InputLabel id={`Title-label`}>
                        Feature Summary
                    </MUI.InputLabel>
                    <div>{props.title}</div>
                    <MUI.InputLabel id={`SelectedFrequency-label`}>
                        Related Module
                    </MUI.InputLabel>
                    <div>{props.selectedModule}</div>
                    <MUI.InputLabel id={`Description-label`}>
                        Feature Description
                    </MUI.InputLabel>
                    <div>{props.description}</div>
                    <MUI.InputLabel id={`Comments-label`}>
                        Additional Comments
                    </MUI.InputLabel>
                    <div>{props.comments}</div>
                </div>
                <div className={`filecontainer`}>
                    {props.attachments.map(
                        (n): JSX.Element => (
                            <FileDisplay fileName={n} key={n} />
                        ),
                    )}
                </div>
            </React.Fragment>
        );
    }

    render(): React.ReactNode {
        return (
            <React.Fragment>
                {this.state.progress === `input` && (
                    <this._requestForm
                        handleCommentsChange={this._handleCommentsChange}
                        handleDescriptionChange={this._handleDescriptionChange}
                        handleModuleChange={this._handleModuleChange}
                        handleTitleChange={this._handleTitleChange}
                        loadFile={this._loadFile}
                        onUploadComplete={this._onUploadComplete}
                        postRequest={this._postRequest}
                        unloadFile={this._unloadFile}
                        {...this.state}
                    />
                )}
                {this.state.progress === `loading` && (
                    <this._awaitingResponce />
                )}
                {this.state.progress === `confirmation` && (
                    <this._requestSubmitted {...this.state} />
                )}
            </React.Fragment>
        );
    }
}
